import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Baby, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface EditPregnancyTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentChildCount: number;
  pregnancyId: string;
  onSave: (childCount: number) => void;
}

export function EditPregnancyTypeModal({
  isOpen,
  onClose,
  currentChildCount,
  pregnancyId,
  onSave,
}: EditPregnancyTypeModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const [childCount, setChildCount] = useState<number>(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const childCountOptions: { value: number; label: string; description: string }[] = [
    { value: 1, label: t('profile:modals.editPregnancyType.singleton'), description: t('profile:modals.editPregnancyType.singletonDesc') },
    { value: 2, label: t('profile:modals.editPregnancyType.twins'), description: t('profile:modals.editPregnancyType.twinsDesc') },
    { value: 3, label: t('profile:modals.editPregnancyType.triplets'), description: t('profile:modals.editPregnancyType.tripletsDesc') },
    { value: 4, label: t('profile:modals.editPregnancyType.quadruplets'), description: t('profile:modals.editPregnancyType.quadrupletsDesc') },
  ];

  // Initialize value when modal opens
  useEffect(() => {
    if (isOpen) {
      setChildCount(currentChildCount);
      setError(null);
    }
  }, [isOpen, currentChildCount]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!pregnancyId) return;

    setIsSaving(true);
    setError(null);

    try {
      // Calculate high-risk based on child count
      const isHighRisk = childCount > 1;

      const { error: dbError } = await supabase
        .from('pregnancies')
        .update({
          child_count: childCount,
          is_high_risk: isHighRisk,
        })
        .eq('id', pregnancyId);

      if (dbError) throw dbError;

      onSave(childCount);
      onClose();
    } catch (err) {
      console.error('Error saving pregnancy type:', err);
      setError(t('profile:modals.editPregnancyType.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Baby className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.editPregnancyType.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('profile:modals.editPregnancyType.description')}
            </p>

            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[10px] text-sm text-red-300">
                {error}
              </div>
            )}

            {/* Child Count Options */}
            <div className="space-y-3">
              {childCountOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setChildCount(option.value)}
                  className={cn(
                    'w-full p-4 rounded-[15px] border text-left transition-all',
                    childCount === option.value
                      ? 'bg-pp-orange/20 border-pp-orange'
                      : 'bg-white/10 border-white/20 hover:bg-white/15'
                  )}
                >
                  <p className="font-medium text-white">{option.label}</p>
                  <p className="text-sm text-white/60">{option.description}</p>
                </button>
              ))}
            </div>

            {/* Info about high-risk */}
            {childCount > 1 && (
              <div className="p-3 bg-amber-500/20 border border-amber-500/30 rounded-[10px] text-sm text-amber-200">
                {t('profile:modals.editPregnancyType.highRiskInfo')}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('common:actions.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
